import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { useMemo } from 'react';
import { cn } from '../../lib/utils';

export interface CitationClickHandler {
  (citationId: string): void;
}

export interface MarkdownRendererProps {
  content: string;
  className?: string;
  /** Called when user clicks an inline [db_X_citN] / [doc_X_citN] / [web_X_citN] / [case_X_citY] link */
  onCitationClick?: CitationClickHandler;
}

/**
 * Render AI-generated markdown with:
 *   - GFM (tables, strikethrough, task lists)
 *   - Citation tokens converted to clickable spans
 *   - Sanitized HTML (we do allow raw HTML because AI sometimes outputs <br/> etc.)
 *
 * Citation token format (from AI agents):
 *   [db_1_cit3]  — legal database chunk
 *   [doc_2_cit1] — user's attached document
 *   [web_4_cit5] — web search result
 *   [case_1_cit2] — user's case documents
 *
 * We pre-process the markdown string to wrap these in <a> tags with a
 * synthetic href="#citation:..." that the link renderer catches.
 */
export function MarkdownRenderer({ content, className, onCitationClick }: MarkdownRendererProps): JSX.Element {
  const processed = useMemo(() => wrapCitations(content), [content]);

  return (
    <div className={cn('prose-sm max-w-none text-sm leading-relaxed text-foreground', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          a: ({ href, children }) => {
            if (href?.startsWith('#citation:')) {
              const id = href.slice('#citation:'.length);
              return (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    onCitationClick?.(id);
                  }}
                  className="inline-flex items-center text-[11px] font-medium text-primary bg-blue-50 hover:bg-blue-100 rounded px-1 py-0.5 mx-0.5 align-baseline"
                >
                  [{citationIndex(id)}]
                </button>
              );
            }
            return (
              <a href={href} target="_blank" rel="noreferrer" className="text-primary underline">
                {children}
              </a>
            );
          },
          p: ({ children }) => <p className="my-2">{children}</p>,
          h1: ({ children }) => <h1 className="text-lg font-semibold mt-4 mb-2">{children}</h1>,
          h2: ({ children }) => <h2 className="text-base font-semibold mt-3 mb-2">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>,
          ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          table: ({ children }) => (
            <div className="overflow-x-auto my-3">
              <table className="min-w-full border-collapse text-xs">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-border bg-muted px-2 py-1 text-left font-medium">{children}</th>
          ),
          td: ({ children }) => <td className="border border-border px-2 py-1 align-top">{children}</td>,
          code: ({ children, className }) => (
            <code className={cn('bg-muted px-1 py-0.5 rounded text-xs', className)}>{children}</code>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-border pl-3 italic text-muted-foreground my-2">
              {children}
            </blockquote>
          ),
        }}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
}

const CITATION_PATTERN = /\[(db|doc|web|case)_(\d+)_cit(\d+)\]/g;

/** Convert [db_1_cit3] → [[db_1_cit3]](#citation:db_1_cit3) so react-markdown renders them as clickable links. */
function wrapCitations(text: string): string {
  return text.replace(CITATION_PATTERN, (match, kind, outer, inner) => {
    const id = `${kind}_${outer}_cit${inner}`;
    // Use a compact numeric label; the real display happens in the custom `a` renderer above.
    return `[${match}](#citation:${id})`;
  });
}

/** Build a short display number from a citation id. Very rough — we'll do proper renumbering later. */
function citationIndex(id: string): string {
  const match = id.match(/_(\d+)_cit(\d+)/);
  if (!match) return '?';
  return `${match[1]}.${match[2]}`;
}
